import { useEffect } from 'react';
import Dropzone from '../../../components/Dropzone/Dropzone';
import { useForm, Controller } from 'react-hook-form';
import {
  Select,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  AlertTitle,
  Typography,
} from '@mui/material';
import { SaveUploadedFiles, DeleteFile } from '../../../api/SecureFileUploader';
import { useMutation } from '@tanstack/react-query';

const SecureFileUploaderRecordModalContent = ({
  sessionGuid,
  handleCancel,
  setHasFiles,
  setFileOpsStatus,
  setSubmitForm,
  onSubmit,
  isDisabled,
  recordID,
  displayName,
}) => {
  const { watch, control, handleSubmit, setValue, getValues, trigger, resetField } = useForm({
    defaultValues: {
      recordID: recordID,
      files: [],
      rsaReq: 1,
      managerReq: 0,
      sendHome: 0,
      guid: sessionGuid,
      displayName: displayName,
    },
  });
  const formValues = watch();
  console.log(formValues);

  const saveUploadedFilesMutation = useMutation({
    mutationFn: (files) => SaveUploadedFiles(files)(),
  });
  const deleteFileMutation = useMutation({
    mutationFn: (fileName) => DeleteFile({ fileName })(),
  });

  useEffect(() => {
    const fileOpsInProgress = saveUploadedFilesMutation.isLoading || deleteFileMutation.isLoading;
    setFileOpsStatus?.(fileOpsInProgress);
  }, [saveUploadedFilesMutation.isLoading, deleteFileMutation.isLoading, setFileOpsStatus]);

  useEffect(() => {
    setSubmitForm(() => handleSubmit(onSubmit));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack gap={2}>
      <Controller
        name="files"
        control={control}
        rules={{
          validate: (value) => (value && value.length > 0) || 'At least one file is required',
        }}
        render={({ field, fieldState: { error } }) => (
          <>
            <Dropzone
              onFilesAccepted={(files) => {
                field.onChange(files);
                setHasFiles(files.length > 0);
              }}
              maxFiles={1}
              accept={{
                'application/pdf': ['.pdf'],
              }}
              dropzoneText="Drag & drop files here, or click to select files"
              clearFiles={null}
              error={!!error}
              onUpload={(files) => {
                const formData = new FormData();
                const renamedFiles = files.map(
                  (file) =>
                    new File([file], `${formValues.guid}${file.name}`, {
                      type: file.type,
                    })
                );
                renamedFiles.forEach((file) => formData.append('files', file));
                console.log(formData);
                return saveUploadedFilesMutation.mutateAsync(formData);
              }}
              onCancel={handleCancel}
              isDeleting={deleteFileMutation.isLoading}
              onDelete={(file) => {
                return deleteFileMutation.mutateAsync(`${formValues.guid}${file.name}`);
              }}
              disabled={isDisabled}
            />
            {error && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {error.message}
              </Typography>
            )}
          </>
        )}
      />
      <Stack direction={'row'} gap={1}>
        <Controller
          name="rsaReq"
          control={control}
          rules={{ required: 'This field is required' }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <InputLabel>Requires RSA Review</InputLabel>
              <Select
                {...field}
                labelId="requires-rsa-select-label"
                label="Requires RSA Review"
                onChange={(e) => {
                  field.onChange(e);

                  if (e.target.value !== 0) {
                    setValue('sendHome', 0);
                  }

                  setValue('rsaReq', e.target.value);
                }}
                error={!!error}
                helperText={error?.message}
                disabled={isDisabled}
              >
                <MenuItem value={1}>Yes</MenuItem>
                <MenuItem value={0}>No</MenuItem>
              </Select>
            </FormControl>
          )}
        />
        {formValues.rsaReq === 0 && (
          <Controller
            name="sendHome"
            control={control}
            rules={{ required: 'This field is required' }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel>Send to Home Office</InputLabel>
                <Select
                  {...field}
                  labelId="sendto-HO-select-label"
                  label="Send to Home Office"
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('sendHome', e.target.value);
                  }}
                  error={!!error}
                  helperText={error?.message}
                  disabled={isDisabled}
                >
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        )}
      </Stack>
      {formValues.rsaReq === 0 && (
        <Alert severity="info">
          <AlertTitle>Notice:</AlertTitle>
          By Sending to Home Office you are acknowledging that a manager has approved the submitted
          document.
        </Alert>
      )}
    </Stack>
  );
};

export default SecureFileUploaderRecordModalContent;
